// src/components/chat/MessageItem.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import styles from './MessageItem.module.css';
import { formatLocalTime } from '../../utils/dateTime';

/**
 * Detects whether a URL points to a common image format.
 * Handles query-string parameters (e.g. ?v=1) and fragments.
 */
const isImageUrl = (url) => {
  if (!url) return false;
  try {
    const { pathname } = new URL(url);
    return /\.(jpg|jpeg|png|gif|webp|avif|svg)$/i.test(pathname);
  } catch {
    // Relative URL — fall back to a simpler check
    return /\.(jpg|jpeg|png|gif|webp|avif|svg)(\?|#|$)/i.test(url);
  }
};

/**
 * Detects an internal post link in a message, e.g. "/post/42" or "/post/uuid".
 * Returns the post ID string, or null if not found.
 */
const extractPostId = (text) => {
  if (!text) return null;
  const match = text.match(/\/post\/([\w-]+)/);
  return match ? match[1] : null;
};

function MessageItem({ msg, isMine }) {
  const postId = extractPostId(msg.message);

  return (
    <div className={`${styles.row} ${isMine ? styles.mine : styles.theirs}`}>
      <div className={styles.bubble}>

        {postId ? (
          /* ── Shared post link ── */
          <Link to={`/post/${postId}`} className={styles.postLink}>
            <div className={styles.postLinkCard}>
              <span className={styles.postLinkIcon} aria-hidden="true">📄</span>
              <div>
                <p className={styles.postLinkTitle}>Shared a Post</p>
                <p className={styles.postLinkSub}>Tap to view</p>
              </div>
              <span className={styles.postLinkArrow} aria-hidden="true">→</span>
            </div>
          </Link>
        ) : (
          <>
            {/* ── Text content ── */}
            {msg.message && <p>{msg.message}</p>}

            {/* ── Media attachment ── */}
            {msg.mediaUrl && (
              <div className={styles.mediaContainer}>
                {isImageUrl(msg.mediaUrl) ? (
                  <img
                    src={msg.mediaUrl}
                    alt="Shared image"
                    className={styles.mediaImage}
                    loading="lazy"
                  />
                ) : (
                  <a
                    href={msg.mediaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.mediaLink}
                  >
                    📎 View attachment
                  </a>
                )}
              </div>
            )}
          </>
        )}

        {/* ── Timestamp + delivery status ── */}
        <span className={styles.time}>
          {msg.sentAt ? formatLocalTime(msg.sentAt) : 'Sending…'}

          {isMine && (
            <span aria-label={
              msg.status === 'sending'
                ? 'Sending'
                : msg.isRead
                ? 'Read'
                : 'Sent'
            }>
              {msg.status === 'sending' && ' ⏳'}
              {msg.status !== 'sending' && !msg.isRead && ' ✓'}
              {msg.isRead && ' ✓✓'}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

// Memoised: the message list re-renders on every keystroke in the input and on
// every cache merge. Without memo, all bubbles re-render each time. Bubbles only
// change when their own message object, id, read-status, or `isMine` changes.
export default React.memo(MessageItem);